/**
 * 換算設定APIクライアント（FKV3-13044・FKV3-13220）
 * バックエンドの /credit/conversion-settings/{設定項目の値} を呼び出す
 */

import { ApiError, apiDelete, apiGet, apiPost, apiPut } from './client';
import type {
  Category,
  ConversionTableName,
  SettingItemKey,
} from '@/lib/credit-conversion-settings/tables';

export interface ConsumptionItemOption {
  value: string;
  label: string;
}

export interface EntrySummary {
  effective_dt: string;
  category: Category;
}

export interface EntryList {
  /** 当日（日本時間）。区分と適用開始日の最小値は、ブラウザの時計でなくこれで決める */
  today: string;
  /** 新しい順 */
  entries: EntrySummary[];
  consumption_items?: ConsumptionItemOption[];
}

/** 1つのテーブルの、その適用開始日の行 */
export interface TableRows {
  table: ConversionTableName;
  /** 軸の昇順。値は画面で表示・入力する値 */
  rows: Record<string, string>[];
}

export interface EntryValue {
  today: string;
  effective_dt: string;
  category: Category;
  /** 編集・削除のときに送り返す */
  version: string;
  /** 設定項目のテーブルごとの行。並びは行の一覧の順 */
  tables: TableRows[];
  consumption_items?: ConsumptionItemOption[];
}

export interface SaveEntryRequest {
  effective_dt: string;
  /** キーはテーブルの物理名 */
  tables: Partial<Record<ConversionTableName, Record<string, string>[]>>;
}

export interface Violation {
  field: 'effective_dt' | null;
  /** テーブルの物理名。行と、行にも入力欄にも属さないエラーの表示先 */
  table: ConversionTableName | null;
  /** そのテーブルの送った行の並びでの番号 */
  row: number | null;
  column: string | null;
  message: string;
}

const base = (item: SettingItemKey) => `/credit/conversion-settings/${item}`;

export function getEntries(item: SettingItemKey, authHeaders?: Record<string, string>) {
  return apiGet<EntryList>(base(item), authHeaders);
}

export function getEntry(
  item: SettingItemKey,
  effectiveDt: string,
  authHeaders?: Record<string, string>,
) {
  return apiGet<EntryValue>(`${base(item)}/${effectiveDt}`, authHeaders);
}

export function createEntry(
  item: SettingItemKey,
  request: SaveEntryRequest,
  authHeaders?: Record<string, string>,
) {
  return apiPost<{ effective_dt: string }>(base(item), request, authHeaders);
}

export function updateEntry(
  item: SettingItemKey,
  originalDt: string,
  request: SaveEntryRequest & { version: string },
  authHeaders?: Record<string, string>,
) {
  return apiPut<{ effective_dt: string }>(`${base(item)}/${originalDt}`, request, authHeaders);
}

export function deleteEntry(
  item: SettingItemKey,
  effectiveDt: string,
  version: string,
  authHeaders?: Record<string, string>,
) {
  const query = new URLSearchParams({ version });
  return apiDelete<void>(`${base(item)}/${effectiveDt}?${query}`, authHeaders);
}

/** サーバーが返した失敗の種類。`NETWORK` はレスポンスが返らなかったとき */
export type FailureCode =
  | 'NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'VALUE_CHANGED'
  | 'NOT_SCHEDULED'
  | 'DATE_CHANGED'
  | 'USER_EMAIL_MISSING'
  | 'CREDIT_DB_UNAVAILABLE'
  | 'SAVE_BUSY'
  | 'NETWORK'
  | 'OTHER';

export interface Failure {
  code: FailureCode;
  /** サーバーが返したメッセージ（なければ HTTP の状態） */
  message: string;
  violations: Violation[];
}

const knownCodes: FailureCode[] = [
  'NOT_FOUND',
  'VALIDATION_ERROR',
  'VALUE_CHANGED',
  'NOT_SCHEDULED',
  'DATE_CHANGED',
  'USER_EMAIL_MISSING',
  'CREDIT_DB_UNAVAILABLE',
  'SAVE_BUSY',
];

/** API の失敗を、画面が表示を分ける種類にする */
export function toFailure(error: unknown): Failure {
  if (error instanceof ApiError) {
    if (error.status === 0) {
      return { code: 'NETWORK', message: error.message, violations: [] };
    }
    const data = (error as ApiError & { data?: Record<string, unknown> }).data ?? {};
    const code = knownCodes.find((c) => c === data.code) ?? 'OTHER';
    const message =
      typeof data.error === 'string' && data.error ? data.error : `HTTP ${error.status}`;
    const violations = Array.isArray(data.violations) ? (data.violations as Violation[]) : [];
    return { code, message, violations };
  }
  return {
    code: 'NETWORK',
    message: error instanceof Error ? error.message : String(error),
    violations: [],
  };
}
